import styled from '@emotion/styled'
import { keyframes } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import CallMergeIcon from '@mui/icons-material/CallMerge'
import { tokens } from '../../theme.js'
import { STATUS_TONE, STATUS_ICON } from '../files/fileStatus.js'
const colors = tokens.colors
const borderRadius = tokens.borderRadius

const turn = keyframes`to { transform: rotate(360deg); }`
export const Spinner = styled.span`
  display: inline-block;
  flex: 0 0 auto;
  box-sizing: border-box;
  width: ${p => p.size || 14}px;
  height: ${p => p.size || 14}px;
  border-radius: 50%;
  border: ${p => p.thickness || 2}px solid ${p => p.tone || colors.status.loading};
  border-right-color: transparent;
  animation: ${turn} 1.4s linear infinite;
`

export const barSx = (tone, { '& .MuiLinearProgress-bar': bar = {}, ...overrides } = {}) => ({
  height: 4,
  borderRadius: 2,
  bgcolor: colors.background.medium,
  ...overrides,
  '& .MuiLinearProgress-bar': { bgcolor: tone, ...bar }
})

const MSG_TONE = {
  success: { fg: colors.status.ready, bg: `${colors.status.ready}1A`, Icon: CheckCircleOutlineIcon },
  info: { fg: colors.primary, bg: colors.primaryExtraLight, Icon: InfoOutlinedIcon },
  error: { fg: colors.error, bg: colors.errorLight, Icon: ErrorOutlineIcon }
}
const MsgInner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  border-radius: ${borderRadius.div};
  background: ${p => p.bg};
  font: 400 14px/1.45 ${tokens.fonts.body};
  color: ${colors.text.body};
  & > .MuiSvgIcon-root {
    font-size: 18px;
    color: ${p => p.fg};
    flex: 0 0 auto;
    margin-top: 1px;
  }
`
export function InlineMessage({ tone = 'info', icon, children, ...rest }) {
  const t = MSG_TONE[tone] || MSG_TONE.info
  const Icon = icon || t.Icon
  return (
    <MsgInner fg={t.fg} bg={t.bg} {...rest}>
      <Icon />
      <div style={{ minWidth: 0, flex: 1 }}>{children}</div>
    </MsgInner>
  )
}

const TONES = {
  raw: colors.status.raw,
  loading: colors.status.loading,
  ready: colors.status.ready,
  done: colors.status.done,
  applied: colors.status.applied,
  failed: colors.error
}
const GLYPHS = {
  check: CheckCircleOutlineIcon,
  merge: CallMergeIcon,
  alert: ErrorOutlineIcon,
  question: HelpOutlineIcon,
  shield: GppGoodOutlinedIcon,
  document: DescriptionOutlinedIcon
}
const PillInner = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: ${p => (p.compact ? '3px 10px' : '5px 15px')};
  border-radius: ${borderRadius.button};
  border: 1px solid ${p => p.tone};
  background: ${p => p.tone}1A;
  white-space: nowrap;
`
const PillIcon = ({ status, tone }) => {
  const glyph = STATUS_ICON[status]
  if (glyph === 'spinner') return <Spinner size={12} tone={tone} data-testid="status-spinner" aria-label="Loading" role="progressbar" />
  const Glyph = GLYPHS[glyph] || DescriptionOutlinedIcon
  return <Glyph sx={{ fontSize: 14, color: tone }} />
}
export function StatusPill({ status, label, compact, ...rest }) {
  const tone = TONES[STATUS_TONE[status]] || colors.status.raw
  return (
    <PillInner tone={tone} compact={compact} {...rest}>
      <PillIcon status={status} tone={tone} />
      <span style={{ font: `${compact ? 500 : 500} ${compact ? 12 : 14}px/1.3 ${tokens.fonts.body}`, color: tone }}>
        {label || String(status || '').toLowerCase()}
      </span>
    </PillInner>
  )
}

const KIND_STYLES = {
  COMBINED: { bg: `${colors.status.done}33`, fg: colors.text.body }
}
const BadgeInner = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  font: 600 11px/1.4 ${tokens.fonts.body};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: ${p => p.bg};
  color: ${p => p.fg};
  white-space: nowrap;
`
export function KindBadge({ kind, ...rest }) {
  const style = KIND_STYLES[kind] || { bg: colors.background.medium, fg: colors.text.body }
  return (
    <BadgeInner bg={style.bg} fg={style.fg} {...rest}>
      {kind}
    </BadgeInner>
  )
}
